import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TextInput } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { shadowStyles } from "../../../styles/shadow";
import FileUploadComponent from "../../file/FileUploadComponent/FileUploadComponent";
import { StoragePathType, File, FileStatus } from "../../../graphql/generated/graphql";
import type { ProcessedFile } from "../../../hooks/useFileUpload";

const SubmitProject = ({ project, onSubmitProject, onBackToRequirements, loading = false, submissionData = null }) => {
  const [uploadedFiles, setUploadedFiles] = useState<(File & { mimeType?: string; fileSize?: number })[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedFiles, setSubmittedFiles] = useState<(File & { mimeType?: string; fileSize?: number })[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [submissionLink, setSubmissionLink] = useState("");
  const [submissionNotes, setSubmissionNotes] = useState("");

  const { showToast } = useToast();

  const handleDeleteFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  // Check if all files have been successfully uploaded
  const hasSuccessfullyUploadedFiles = () => {
    return uploadedFiles.length > 0;
  };

  const handleSubmit = async () => {
    if (uploadedFiles.length === 0) {
      showToast({
        message: "Missing Documents. Please upload at least one document to submit your project.",
        type: "error",
      });
      return;
    }

    try {
      setIsUploading(true);

      const fileInputs = uploadedFiles.map((file) => ({
        fileId: file.id,
        fileName: file.fileName,
        fileURL: file.fileURL,
        contentType: file.mimeType,
        size: file.fileSize,
      }));

      setSubmittedFiles([...uploadedFiles]);
      setHasSubmitted(true);

      const submission = {
        submissionFiles: fileInputs,
        projectId: project?.id,
        submissionLink: submissionLink || null,
        submissionNotes: submissionNotes || null,
      };

      onSubmitProject(submission);
    } catch (error) {
      console.error("Submit error:", error);
      showToast({
        message: `Submit Error: Failed to submit project: ${error.message}`,
        type: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const isResubmission = submissionData?.status === "SUBMITTED" || submissionData?.status === "REJECTED";
  const isSubmitDisabled = uploadedFiles.length === 0 || loading || isUploading || !hasSuccessfullyUploadedFiles();

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content} showsVerticalScrollIndicator={true}>
      {/* Info Box */}
      <View style={[shadowStyles.smallShadow, styles.infoBox]}>
        <View style={{ marginRight: 10 }}>
          <CustomText style={styles.infoIcon} text="ℹ️" useTranslationText={false} />
        </View>
        <View style={{ flex: 1 }}>
          <CustomText
            style={styles.infoBoxText}
            size="s"
            text={
              hasSubmitted
                ? "Project submitted successfully! Your submission has been recorded and is under review."
                : isResubmission
                  ? "You are resubmitting your project. Please upload updated documents and provide any additional notes."
                  : "Ready to submit your project? Upload your completed documents and deliverables. This will mark your project as submitted for final review."
            }
            useTranslationText={false}
            accessibilityLabel="Project submission information"
          />
        </View>
      </View>

      {/* Project Info */}
      <View style={styles.projectInfo}>
        <CustomText
          style={styles.projectTitle}
          text={project?.name || "Project Submission"}
          size="l"
          weight="bold"
          useTranslationText={false}
          aria-level="2"
          role="heading"
        />
        {project?.description && (
          <CustomText
            style={styles.projectDescription}
            text={project.description}
            size="m"
            useTranslationText={false}
          />
        )}
      </View>

      {/* File Upload Section - Hide after submission */}
      {!hasSubmitted && (
        <View style={styles.uploadSection}>
          <View style={styles.uploadHeader}>
            <CustomText style={styles.uploadIcon} text="📁" useTranslationText={false} />
            <CustomText
              style={styles.uploadTitle}
              text="Upload Project Documents"
              size="m"
              weight="bold"
              useTranslationText={false}
              aria-level="2"
              role="heading"
            />
          </View>
          <CustomText
            style={styles.uploadDescription}
            text="Upload your completed project files, documentation, reports, or any other deliverables required for submission."
            size="s"
            useTranslationText={false}
          />

          <FileUploadComponent
            storagePath={StoragePathType.ProjectSubmissions}
            beforeUpload={() => {
              setIsUploading(true);
              setUploadError(null);
            }}
            afterUpload={(processedFiles: ProcessedFile[]) => {
              const newFiles = processedFiles.map((processedFile) => ({
                ...processedFile.file,
                status: FileStatus.Pending,
                mimeType: processedFile.mimeType,
                fileSize: processedFile.size,
              }));
              setUploadedFiles((prev) => [...prev, ...newFiles]);
              setIsUploading(false);
            }}
            onError={(error: string) => {
              setUploadError(error);
              setIsUploading(false);
              showToast({
                message: `Upload Error: ${error}`,
                type: "error",
              });
            }}
          />

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <View style={styles.filesList}>
              <CustomText
                text="Uploaded Files:"
                size="s"
                weight="bold"
                style={styles.filesListTitle}
                useTranslationText={false}
              />
              {uploadedFiles.map((file, index) => (
                <View key={file.id || index} style={styles.fileItem}>
                  <CustomText style={styles.fileIcon} text="📄" useTranslationText={false} />
                  <CustomText
                    style={styles.fileName}
                    text={file.fileName || `Document ${index + 1}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <CustomButton
                    text="Remove"
                    onPress={() => handleDeleteFile(file.id)}
                    size="xs"
                    styleType="secondary"
                    style={styles.removeButton}
                  />
                </View>
              ))}
            </View>
          )}

          {uploadError && (
            <CustomText text={uploadError} size="s" style={styles.errorText} useTranslationText={false} />
          )}
        </View>
      )}

      {/* Link Input Section - Hide after submission */}
      {!hasSubmitted && (
        <View style={styles.inputSection}>
          <CustomText
            text="Add a Link"
            size="m"
            weight="bold"
            style={styles.inputLabel}
            useTranslationText={false}
            aria-level="2"
            role="heading"
          />
          <CustomText
            text="Add a shareable link (Google Drive, Dropbox, GitHub, etc.). Make sure your link has view access enabled."
            size="s"
            style={styles.inputDescription}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <CustomTextInput
            value={submissionLink}
            onChangeText={setSubmissionLink}
            placeholder="https://drive.google.com/..."
            style={styles.textInput}
          />
        </View>
      )}

      {/* Notes Input Section - Hide after submission */}
      {!hasSubmitted && (
        <View style={styles.inputSection}>
          <CustomText
            text="Notes (Optional)"
            size="m"
            weight="bold"
            style={styles.inputLabel}
            useTranslationText={false}
            aria-level="2"
            role="heading"
          />
          <CustomText
            text="Add any special instructions, context, or clarifications for your instructor."
            size="s"
            style={styles.inputDescription}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <TextInput
            value={submissionNotes}
            onChangeText={setSubmissionNotes}
            placeholder="Add any additional context or instructions..."
            style={styles.textArea}
            multiline={true}
            numberOfLines={4}
            textAlignVertical="top"
          />
        </View>
      )}

      {/* Submitted Files Section - Show after submission */}
      {hasSubmitted && submittedFiles.length > 0 && (
        <View style={styles.submittedSection}>
          <View style={styles.submittedHeader}>
            <CustomText style={styles.checkIcon} text="✅" useTranslationText={false} />
            <CustomText
              style={styles.submittedTitle}
              text="Submitted Documents"
              size="m"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          <CustomText
            style={styles.submittedDescription}
            text="The following files have been successfully submitted:"
            size="s"
            useTranslationText={false}
          />
          <View style={styles.filesList}>
            {submittedFiles.map((file, index) => (
              <View key={file.id || index} style={styles.fileItem}>
                <CustomText style={styles.fileIcon} text="📄" useTranslationText={false} />
                <CustomText
                  style={styles.fileName}
                  text={file.fileName || `Document ${index + 1}`}
                  size="s"
                  useTranslationText={false}
                />
                <View style={styles.completedBadge}>
                  <CustomText style={styles.completedText} text="SUBMITTED" useTranslationText={false} />
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Submitted Link and Notes - Show after submission */}
      {hasSubmitted && (submissionLink || submissionNotes) && (
        <View style={styles.submittedSection}>
          <View style={styles.submittedHeader}>
            <CustomText style={styles.checkIcon} text="✅" useTranslationText={false} />
            <CustomText
              style={styles.submittedTitle}
              text="Additional Information"
              size="m"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          {submissionLink && (
            <View style={styles.submissionInfo}>
              <CustomText text="Link:" size="s" weight="bold" style={styles.infoLabel} useTranslationText={false} />
              <CustomText text={submissionLink} size="s" style={styles.infoValue} useTranslationText={false} />
            </View>
          )}
          {submissionNotes && (
            <View style={styles.submissionInfo}>
              <CustomText text="Notes:" size="s" weight="bold" style={styles.infoLabel} useTranslationText={false} />
              <CustomText text={submissionNotes} size="s" style={styles.infoValue} useTranslationText={false} />
            </View>
          )}
        </View>
      )}

      {/* Previous Submissions */}
      {submissionData && (
        <View style={styles.previousSubmissions}>
          <CustomText
            style={styles.sectionTitle}
            text="Previous Submission"
            size="m"
            weight="bold"
            useTranslationText={false}
            aria-level="3"
            role="heading"
          />
          <View style={styles.submissionCard}>
            <View style={styles.submissionHeader}>
              <CustomText text={`Status: ${submissionData.status}`} size="s" weight="bold" useTranslationText={false} />
              {submissionData.submittedAt && (
                <CustomText
                  text={`Submitted: ${new Date(submissionData.submittedAt).toLocaleDateString()}`}
                  size="xs"
                  useTranslationText={false}
                />
              )}
            </View>
            {submissionData.feedback && (
              <View style={styles.feedbackSection}>
                <CustomText text="Feedback:" size="s" weight="bold" useTranslationText={false} />
                <CustomText
                  text={submissionData.feedback}
                  size="s"
                  style={styles.feedbackText}
                  useTranslationText={false}
                />
              </View>
            )}
          </View>
        </View>
      )}

      {/* Action Buttons */}
      <View style={styles.buttonContainer}>
        {!hasSubmitted && (
          <CustomButton
            text={
              isSubmitDisabled && uploadedFiles.length === 0
                ? "Upload files to submit"
                : isSubmitDisabled && (isUploading || !hasSuccessfullyUploadedFiles())
                  ? "Waiting for upload..."
                  : isResubmission
                    ? "Resubmit Project"
                    : "Submit Project"
            }
            onPress={handleSubmit}
            size="m"
            styleType={isSubmitDisabled ? "secondary" : "primary"}
            style={[styles.submitButton, isSubmitDisabled && styles.submitButtonDisabled]}
            textStyle={[styles.submitButtonText, isSubmitDisabled && styles.submitButtonTextDisabled]}
            loading={loading || isUploading}
            disabled={isSubmitDisabled}
            aria-label={isResubmission ? "Resubmit Project" : "Submit Project"}
            accessibilityHint="Press to submit your project documents for review"
          />
        )}
        {hasSubmitted && (
          <CustomButton
            text="Project Completed ✅"
            onPress={onBackToRequirements}
            size="m"
            style={styles.completedButton}
            bold={true}
            disabled={loading}
            aria-label="Project Completed - Close Modal"
          />
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  infoBox: {
    padding: 16,
    backgroundColor: "rgba(91, 224, 104, 0.1)",
    borderRadius: 10,
    marginBottom: 24,
    flexDirection: "row",
    alignItems: "flex-start",
    borderWidth: 1,
    borderColor: "rgba(91, 224, 104, 0.3)",
  },
  infoBoxText: {
    color: "#000000",
    lineHeight: 20,
  },
  projectInfo: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  projectTitle: {
    color: "#101828",
    marginBottom: 8,
  },
  projectDescription: {
    color: "#667085",
    lineHeight: 22,
  },
  uploadSection: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  uploadHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  uploadIcon: {
    marginRight: 8,
    color: "#667085",
  },
  uploadTitle: {
    color: "#101828",
  },
  uploadDescription: {
    color: "#667085",
    marginBottom: 16,
    lineHeight: 20,
  },
  previousSubmissions: {
    marginBottom: 24,
  },
  sectionTitle: {
    color: "#101828",
    marginBottom: 12,
  },
  submissionCard: {
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  submissionHeader: {
    marginBottom: 12,
  },
  feedbackSection: {
    marginTop: 12,
  },
  feedbackText: {
    color: "#667085",
    marginTop: 4,
    fontStyle: "italic",
  },
  buttonContainer: {
    flexDirection: "column",
    gap: 12,
  },
  submitButton: {
    // Remove custom background - let CustomButton handle styling
  },
  submitButtonText: {
    color: "#ffffff",
    fontWeight: "600",
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonTextDisabled: {
    color: "#9CA3AF",
  },
  backButton: {
    borderColor: "#3E68FE",
    borderWidth: 2,
    borderRadius: 10,
  },
  infoIcon: {
    fontSize: 16,
  },
  submittedSection: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#F0FDF4",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#BBF7D0",
  },
  submittedHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  checkIcon: {
    marginRight: 8,
    fontSize: 16,
  },
  submittedTitle: {
    color: "#101828",
  },
  submittedDescription: {
    color: "#667085",
    marginBottom: 16,
    lineHeight: 20,
  },
  filesList: {
    marginTop: 16,
  },
  filesListTitle: {
    color: "#101828",
    marginBottom: 8,
  },
  fileItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  fileIcon: {
    marginRight: 12,
    fontSize: 16,
  },
  fileName: {
    flex: 1,
    color: "#374151",
  },
  completedBadge: {
    backgroundColor: "#10B981",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  completedText: {
    color: "#ffffff",
    fontSize: 10,
    fontWeight: "600",
  },
  completedButton: {
    backgroundColor: "#10B981",
    color: "#fff",
  },
  inputSection: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  inputLabel: {
    color: "#101828",
    marginBottom: 4,
  },
  inputDescription: {
    color: "#667085",
    lineHeight: 20,
  },
  textInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#1F2937",
  },
  textArea: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: "#1F2937",
    minHeight: 100,
  },
  removeButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  errorText: {
    color: "#DC2626",
    marginTop: 8,
  },
  submissionInfo: {
    marginBottom: 12,
  },
  infoLabel: {
    color: "#101828",
    marginBottom: 4,
  },
  infoValue: {
    color: "#667085",
    lineHeight: 20,
  },
});

export default SubmitProject;
